import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ContactRequest {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  contact_preference: 'email' | 'phone' | 'either';
  message: string;
  status: 'pending' | 'in_progress' | 'completed' | 'archived';
  created_at: string;
  updated_at: string;
  responded_at: string | null;
  responded_by: string | null;
  internal_notes: string | null;
}

interface ContactRequestModalProps {
  request: ContactRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export function ContactRequestModal({ request, isOpen, onClose, onUpdate }: ContactRequestModalProps) {
  const [status, setStatus] = useState(request?.status || 'pending');
  const [internalNotes, setInternalNotes] = useState(request?.internal_notes || '');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (request) {
      setStatus(request.status);
      setInternalNotes(request.internal_notes || '');
    }
  }, [request]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleSave = async () => {
    if (!request) return;

    try {
      const { error } = await supabase.rpc('update_contact_request_with_notes', {
        request_id: request.id,
        new_status: status,
        new_internal_notes: internalNotes || null,
      });

      if (error) throw error;

      setSuccess('Contact request updated successfully');
      onUpdate(); // Refresh the parent table
    } catch (err: any) {
      console.error('Error updating contact request:', err);
      setError('Failed to update contact request');
    }
  };

  const handleMarkAsComplete = async () => {
    if (!request) return;

    try {
      setStatus('completed'); // Update the status in the UI immediately
      const { error } = await supabase.rpc('update_contact_request_with_notes', {
        request_id: request.id,
        new_status: 'completed',
        new_internal_notes: internalNotes || null,
      });

      if (error) throw error;

      setSuccess('Contact request marked as complete');
      onUpdate(); // Refresh the parent table
      setTimeout(() => onClose(), 1000); // Close the modal after 1 second
    } catch (err: any) {
      console.error('Error marking contact request as complete:', err);
      setError('Failed to mark contact request as complete');
    }
  };

  if (!isOpen || !request) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal Content */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Contact Request Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* Alerts */}
            {error && (
              <div className="px-4 py-3 bg-red-50 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="ml-3 text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
            {success && (
              <div className="px-4 py-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-400" />
                  <p className="ml-3 text-sm text-green-700">{success}</p>
                </div>
              </div>
            )}

            {/* Contact Request Details */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <p className="mt-1 text-sm text-gray-900">{request.first_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <p className="mt-1 text-sm text-gray-900">{request.last_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <p className="mt-1 text-sm text-gray-900">{request.email || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <p className="mt-1 text-sm text-gray-900">{request.phone || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Preference</label>
                <p className="mt-1 text-sm text-gray-900">{request.contact_preference}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <p className="mt-1 text-sm text-gray-900">{request.message}</p>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Internal Notes</label>
                <textarea
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  rows={4}
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Add internal notes..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Created At</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(request.created_at).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Updated At</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(request.updated_at).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Responded At</label>
                <p className="mt-1 text-sm text-gray-900">
                  {request.responded_at
                    ? new Date(request.responded_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Responded By</label>
                <p className="mt-1 text-sm text-gray-900">{request.responded_by || '-'}</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end p-5 border-t border-gray-200">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 mr-3"
            >
              Cancel
            </button>
            {status !== 'completed' && (
              <button
                onClick={handleMarkAsComplete}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 mr-3"
              >
                Mark as Complete
              </button>
            )}
            <button
              onClick={handleSave}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}